import { GraphQLClient } from 'graphql-request';

import {
  CACHE_GRAPHQL_API_URI,
  ED_GRAPHQL_API_URI,
  GRAPHQL_API_URI,
  GRAPHQL_READ_API_URI,
  GRAPHQL_READ_FIN_API_URI,
  RESIZE_GRAPHQL_API_URI,
  TEST_GRAPHQL_API_URI,
  UPLOADS_API_URI
} from '../../../config';
import {
  graphQLCachePath,
  graphQLReadFinPath
} from '../../../api/common/backendGraphQLClients';

class CustomGraphQLClient extends GraphQLClient {
  private endpoint: string;

  constructor(endpoint: string, options?: RequestInit) {
    super(endpoint, options);
    this.endpoint = endpoint;
  }

  // requestId is deliberately NOT added to the URL any more.
  //
  // Why: the browser calls api/cache/ed/... cross-origin, so every POST needs a
  // CORS preflight (OPTIONS). The browser caches a preflight per exact URL
  // (Chrome up to 2 h). With a fresh requestId in the query string every URL
  // was unique, the cache never hit, and EVERY request paid an extra OPTIONS
  // round trip — ~100 ms from New York, ~165 ms from California to Hetzner,
  // on every wave of requests of every page.
  //
  // Nothing is lost: all callers already send the same id in the `requestId`
  // header (third argument of `request()`), which all backends allow in CORS.
  // queryName stays in the URL: there are few distinct names, so each gets
  // one cached preflight, and nginx access logs keep showing the query name.
  //
  // Readers of the old URL parameter, and where they read the id now:
  //   - graphql.archivizer.com used `?requestId=` as its request id and
  //     forwarded it to bit/inbox; it now reads the `requestid` header
  //     (graphql.archivizer.com#1778, query kept as a fallback).
  //   - nginx access logs showed it as part of the URL (`"$request"`); they
  //     need `$http_requestid` in log_format.
  // api, cache, ed and bit adopt the header as their own request id
  // (log tag, X-Request-Id response header).
  //
  // The requestId argument is kept so the 30 callers stay unchanged.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addQueryParams(queryName: string, requestId: string) {
    // this.setEndpoint(
    //   `${this.endpoint}?queryName=${queryName}&requestId=${requestId}`
    // );
    this.setEndpoint(`${this.endpoint}?queryName=${queryName}`);
  }
}

export const graphQLTestClient = new CustomGraphQLClient(TEST_GRAPHQL_API_URI, {
  credentials: 'include',
  mode: 'cors'
});

export const graphQLClient = new CustomGraphQLClient(GRAPHQL_API_URI, {
  credentials: 'include',
  mode: 'cors'
});

export const graphQLReadClient = new CustomGraphQLClient(GRAPHQL_READ_API_URI, {
  credentials: 'include',
  mode: 'cors'
});

export const graphQLCacheClient = new CustomGraphQLClient(
  CACHE_GRAPHQL_API_URI,
  {
    credentials: 'include',
    mode: 'cors'
  }
);

export const graphQLResizeClient = new CustomGraphQLClient(
  RESIZE_GRAPHQL_API_URI,
  {
    credentials: 'include',
    mode: 'cors'
  }
);

export const graphQLEdClient = new CustomGraphQLClient(ED_GRAPHQL_API_URI, {
  credentials: 'include',
  mode: 'cors'
});

export const graphQLReadFinClient = new CustomGraphQLClient(
  GRAPHQL_READ_FIN_API_URI,
  {
    credentials: 'include',
    mode: 'cors'
  }
);

export const graphQLReadFinBackendClient = new CustomGraphQLClient(
  graphQLReadFinPath,
  {
    credentials: 'include',
    mode: 'cors'
  }
);

export const graphQLCacheBackendClient = new CustomGraphQLClient(
  graphQLCachePath,
  {
    credentials: 'include',
    mode: 'cors'
  }
);

export const uploadsClient = new CustomGraphQLClient(
  UPLOADS_API_URI || GRAPHQL_API_URI,
  {
    credentials: 'include',
    mode: 'cors'
  }
);
